import type { Adapter, IpcHandler, StoredAuthData } from '@frontend/shell-utils';
import type { StorageChangeCallback, StorageChangeField, TokenStorageStrategy } from './TokenStorageStrategy';

/**
 * Minimal shape of the Electron shell bridge we depend on. Defined locally so tests
 * can supply a fake shell without pulling in the full `@frontend/shell-utils` runtime.
 */
export type ShellBridge = Pick<Adapter, 'on' | 'off' | 'emit' | 'invokeSync'>;

const TOKEN_CHANGED_LISTENER_ID = 'electron-token-storage-strategy';

/**
 * TokenStorageStrategy implementation for Electron renderers. Tokens are owned by the
 * main process (refresh token in OS-encrypted storage via safeStorage; weave/jti tokens
 * held in-memory). This strategy exposes them to NWX through the `window.shell` IPC bridge.
 *
 * Read model: synchronous reads from an in-memory cache, hydrated once at init via a
 * synchronous IPC round-trip, then kept fresh by subscribing to `auth:token-changed` that
 * the main process emits when it has new tokens. At first glance, this seems really redundant
 * with what TokenStorage is doing with its own caching - why not just have TokenStorage call
 * `invokeSync` on every read? The main reason is that we want to avoid unnecessary IPC round-trips
 * on hot code paths like `getToken()`; by keeping a local cache in the strategy we can ensure that
 * once initialized, all reads are just fast local memory access.
 *
 * Write model: the renderer is NOT the source of truth for tokens. `setToken`/`setJTI`/
 * `setTokenReceivedAt` update the local cache (so `clearCache()` → re-read works)
 */
export class ElectronTokenStorageStrategy implements TokenStorageStrategy {
  public name = 'ElectronTokenStorageStrategy';

  private cache: StoredAuthData = {
    token: undefined,
    jti: undefined,
    tokenReceivedAt: undefined,
  };

  constructor(private shell: ShellBridge) {}

  init() {
    this.hydrate();
    return this;
  }

  private hydrate(): void {
    // if no handler is available in main for this channel, invokeSync will return undefined
    const data = this.shell.invokeSync('auth:get-stored-auth-data') as StoredAuthData | null | undefined;
    if (data) {
      this.cache = {
        token: data.token,
        jti: data.jti,
        tokenReceivedAt: data.tokenReceivedAt,
      };
    } else {
      console.warn('ElectronTokenStorageStrategy: no stored auth data received from main process, user is logged out');
    }
  }

  getToken(): string | undefined {
    return this.cache.token;
  }

  /**
   * Updates the local cache only — does NOT write to main. In the Electron auth flow main
   * is the source of truth for tokens; this is here for interface compliance (TokenStorage
   * calls setToken/setJTI/setTokenReceivedAt together).
   */
  setToken(token: string): void {
    console.warn('ElectronTokenStorageStrategy.setToken: cache-only, not writing to main.');
    this.cache.token = token;
  }

  removeToken(): void {
    this.cache.token = undefined;
  }

  getJTI(): string | undefined {
    return this.cache.jti;
  }

  setJTI(jti: string): void {
    console.warn('ElectronTokenStorageStrategy.setJTI: cache-only, not writing to main.');
    this.cache.jti = jti;
  }

  removeJTI(): void {
    this.cache.jti = undefined;
  }

  getTokenReceivedAt(): number | undefined {
    return this.cache.tokenReceivedAt;
  }

  setTokenReceivedAt(value: number): void {
    console.warn('ElectronTokenStorageStrategy.setTokenReceivedAt: cache-only, not writing to main.');
    this.cache.tokenReceivedAt = value;
  }

  removeTokenReceivedAt(): void {
    this.cache.tokenReceivedAt = undefined;
  }

  clearStoredAuthData(): void {
    this.cache = { token: undefined, jti: undefined, tokenReceivedAt: undefined };
    // NOTE notifying the main process of logout (via `auth:clear-stored-auth-data` IPC)
    // is the responsibility of the `Authn` orchestrator,not this storage strategy.
  }

  /**
   * Subscribe to token changes initiated by the main process (proactive refresh, logout,
   * etc.). Diffs the incoming payload against our cache and fires the callback once per
   * changed field — mirroring how BrowserTokenStorageStrategy reports per-field changes
   * from the `storage` event.
   */
  onStorageChange(cb: StorageChangeCallback): () => void {
    const handler: IpcHandler<'auth:token-changed'> = (_e, data) => {
      const prev = this.cache;
      const next: StoredAuthData = data ?? {
        token: undefined,
        jti: undefined,
        tokenReceivedAt: undefined,
      };
      this.cache = { ...next };

      const fire = (field: StorageChangeField, newValue: unknown) => {
        const serialized =
          newValue === undefined ? null : typeof newValue === 'string' ? newValue : JSON.stringify(newValue);
        cb(field, serialized);
      };
      if (prev.token !== next.token) fire('token', next.token);
      if (prev.jti !== next.jti) fire('jti', next.jti);
    };

    this.shell.on('auth:token-changed', handler, TOKEN_CHANGED_LISTENER_ID);
    return () => {
      this.shell.off('auth:token-changed', handler, TOKEN_CHANGED_LISTENER_ID);
    };
  }
}
